import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { isTauriRuntime } from "src/lib/runtime/environment";
import { useDesktopNavigationStore } from "src/stores/desktopNavigationStore";
import { hasOpenModal, isInputElement } from "src/utilities/keyboard";

const MOUSE_BACK_BUTTON = 3;
const MOUSE_FORWARD_BUTTON = 4;

const getHref = (location: ReturnType<typeof useLocation>): string =>
  `${location.pathname}${location.search}${location.hash}`;

const shouldIgnoreShortcut = (target: EventTarget | null): boolean =>
  isInputElement(target) || hasOpenModal();

export function useDesktopNavigationTracker() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const recordLocation = useDesktopNavigationStore((state) => state.recordLocation);

  useEffect(() => {
    if (!isTauriRuntime()) {
      return;
    }

    recordLocation(
      {
        key: location.key || getHref(location),
        href: getHref(location),
      },
      navigationType
    );
  }, [location, navigationType, recordLocation]);
}

export function useDesktopNavigation() {
  const navigate = useNavigate();
  const canGoBack = useDesktopNavigationStore((state) => state.canGoBack);
  const canGoForward = useDesktopNavigationStore((state) => state.canGoForward);

  const goBack = useCallback(() => {
    if (canGoBack) {
      navigate(-1);
    }
  }, [canGoBack, navigate]);

  const goForward = useCallback(() => {
    if (canGoForward) {
      navigate(1);
    }
  }, [canGoForward, navigate]);

  return {
    isAvailable: isTauriRuntime(),
    canGoBack,
    canGoForward,
    goBack,
    goForward,
  };
}

export function useDesktopNavigationShortcuts() {
  const { isAvailable, canGoBack, canGoForward, goBack, goForward } = useDesktopNavigation();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isAvailable || shouldIgnoreShortcut(event.target)) {
        return;
      }

      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }

      if (event.key === "[" && canGoBack) {
        event.preventDefault();
        goBack();
      } else if (event.key === "]" && canGoForward) {
        event.preventDefault();
        goForward();
      } else if (event.key === "r") {
        event.preventDefault();
        window.location.reload();
      }
    },
    [canGoBack, canGoForward, goBack, goForward, isAvailable]
  );

  const handleMouseUp = useCallback(
    (event: MouseEvent) => {
      if (!isAvailable || shouldIgnoreShortcut(event.target)) {
        return;
      }

      if (event.button === MOUSE_BACK_BUTTON && canGoBack) {
        event.preventDefault();
        goBack();
      } else if (event.button === MOUSE_FORWARD_BUTTON && canGoForward) {
        event.preventDefault();
        goForward();
      }
    },
    [canGoBack, canGoForward, goBack, goForward, isAvailable]
  );

  useEffect(() => {
    if (!isAvailable) {
      return;
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mouseup", handleMouseUp, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mouseup", handleMouseUp, true);
    };
  }, [handleKeyDown, handleMouseUp, isAvailable]);
}
